/* Copyright (c) 2020-present CloudIO Inc */

import { DBRow, Row } from '@cloudio-saas/datasource-types';
import { TextField as MUITextField, TextFieldProps } from '@mui/material';
import React, { useRef } from 'react';
import { isEmpty } from 'utils/isEmpty';
import type { Validations } from 'utils/validators';

const TextField = (
  props: Omit<TextFieldProps, 'variant' | 'fullWidth' | 'autoComplete'> & {
    'data-testid': string;
  },
): React.ReactElement => {
  return (
    <MUITextField
      variant="standard"
      fullWidth
      autoComplete="off"
      {...props}
      data-testid={`io-${props['data-testid']}`}
      inputProps={{
        ...props.inputProps,
        'data-testid': `io-${props['data-testid']}-input`,
      }}
    />
  );
};

interface Props<T>
  extends Omit<TextFieldProps, 'variant' | 'fullWidth' | 'autoComplete'> {
  record: Row<T>;
  attribute: keyof T;
  update: (partialRecord: Partial<DBRow<T>>) => void;
  validations: Validations<T>;
  validate?: (focus: boolean) => string;
  'data-testid': string;
}

export function ValidatedTextField<T>(props: Props<T>): React.ReactElement {
  const {
    record,
    validate,
    validations,
    attribute,
    update,
    required,
    ...others
  } = props;
  const ref = useRef<HTMLInputElement>(null);
  validations.register(attribute, (focus) => {
    let msg = '';
    if (required && isEmpty(record[attribute])) {
      msg = 'Value required!';
    } else {
      msg = validate ? validate(focus) : '';
    }
    if (focus && msg) {
      ref.current?.focus();
    }
    return msg;
  });
  return (
    <TextField
      {...others}
      required={required}
      value={record[attribute] ?? ''}
      onChange={(e) =>
        update({ [attribute]: e.target.value } as Partial<DBRow<T>>)
      }
      inputRef={ref}
      {...validations.props(attribute)}
    />
  );
}

export default TextField;
